<script setup lang="ts">
  import { getCurrentInstance, onMounted, ref } from 'vue';
  import SimpleScrollableModal from './base/SimpleScrollableModal.vue';
  import { IRestaurant } from '@/interfaces/IRestaurant';
  import {
    loadChangelogMarkdown,
    renderChangelogHtml,
  } from '@/domain/changelog/changelog';

  const props = defineProps<{
    restaurant?: IRestaurant;
  }>();

  const emit = defineEmits<{
    (e: 'close'): void;
  }>();

  // i18n/router in script context (script setup has no $t/$router)
  const proxy = getCurrentInstance()!.proxy;

  const html = ref('');
  const loadFailed = ref(false);

  onMounted(async () => {
    try {
      const md = await loadChangelogMarkdown(proxy.$i18n.locale);
      html.value = renderChangelogHtml(
        md,
        props.restaurant ? `/${props.restaurant.stringId}` : ''
      );
    } catch (error) {
      console.error('Failed to load changelog:', error);
      loadFailed.value = true;
    }
  });

  // v-html content can't use <router-link>, so intercept anchor clicks:
  // internal (app-relative) hrefs go through the router and close the modal,
  // external ones open in a new tab, mailto keeps default navigation.
  const onContentClick = (event: MouseEvent) => {
    const anchor = (event.target as HTMLElement).closest('a');
    if (!anchor) return;
    const href = anchor.getAttribute('href');
    if (!href || href.startsWith('mailto:')) return;
    event.preventDefault();
    if (href.startsWith('/')) {
      // Swallow NavigationDuplicated, like <router-link> did.
      proxy.$router.push(href).catch(() => undefined);
      emit('close');
    } else {
      window.open(href, '_blank', 'noopener');
    }
  };
</script>

<template>
  <simple-scrollable-modal>
    <template #title>
      <h3>{{ $t('changelog.title') }}</h3>
    </template>
    <template #content>
      <p v-if="loadFailed" class="changelog-error">
        {{ $t('errors.tryAgain') }}
      </p>
      <div
        v-else
        class="changelog-content"
        v-html="html"
        @click="onContentClick"
      ></div>
    </template>
    <template #action-bar>
      <div class="d-flex flex-row justify-end">
        <v-btn color="info" depressed @click="emit('close')">
          {{ $t('close') }}
        </v-btn>
      </div>
    </template>
  </simple-scrollable-modal>
</template>

<style scoped lang="scss">
  .changelog-error {
    @apply tw-my-2;
  }

  .changelog-content {
    ::v-deep h2 {
      @apply tw-text-base tw-font-semibold tw-mt-4 tw-mb-1;

      &:first-child {
        @apply tw-mt-0;
      }
    }

    ::v-deep p {
      @apply tw-my-2;
    }

    ::v-deep ul {
      @apply tw-my-2 tw-pl-5;
      list-style-type: disc;

      li {
        @apply tw-mb-1;
      }
    }

    ::v-deep a {
      @apply tw-text-blue-600 tw-underline;
    }
  }
</style>
